import React, { createContext, PropsWithChildren, useContext } from "react";
import { Box, Flex, HStack, Icon, Text, VStack } from "@chakra-ui/react";
import { Link, useSearchParams } from "react-router-dom";
import { SIDEBAR_ITEMS } from "../../constants/site-builder";
import { useQuery, UseQueryResult } from "react-query";
import { __ } from "@wordpress/i18n";
import apiFetch from "@wordpress/api-fetch";
import { Templates } from "./components/Templates";
import { HomeIcon } from "./constants/icons";

export type Template = {
	id: string;
	[k: string]: any;
};

export type Templates = Array<Template>;

const SiteBuilderContext = createContext<{
	type: string;
	query: UseQueryResult<
		{
			[k: string]: any;
			id: number;
		}[],
		unknown
	>;
} | null>(null);

export const SiteBuilderProvider = (props: PropsWithChildren) => {
	const [searchParams] = useSearchParams();
	const type = searchParams.get("type") || "all";
	const query = useQuery({
		queryKey: ["templates"],
		queryFn: () => {
			return apiFetch<
				{
					id: number;
					[k: string]: any;
				}[]
			>({
				path: "magazine-blocks/v1/site-builder",
			});
		},
	});

	return (
		<SiteBuilderContext.Provider
			value={{
				type: typeof type === "undefined" ? "all" : type,
				query,
			}}
		>
			{props.children}
		</SiteBuilderContext.Provider>
	);
};

export const useSiteBuilder = () => {
	const context = useContext(SiteBuilderContext);
	if (!context) {
		throw new Error(
			"useSiteBuilder should be used within SiteBuilderProvider"
		);
	}
	return context;
};

const SiteBuilderNav = () => {
	const { type } = useSiteBuilder();
	return (
		<Box w="260px" borderRight="1px" borderColor="#E0E0E0" py={6}>
			<VStack align="stretch" spacing={1} padding="0 20px">
				<HStack
					as={Link}
					to={`/site-builder?type=all`}
					role="group"
					transition="all 0.2s"
					w="full"
					py={2}
					px={4}
					gap="8px"
					bgColor={type === "all" ? "purple.50" : "transparent"}
					color={type === "all" ? "#690AA0" : "gray.700"}
					borderRadius="md"
					cursor="pointer"
					spacing={3}
					_active={{
						bgColor: "#F7F2FA",
						color: "#690AA0",
					}}
					_hover={{
						bgColor: type === "all" ? "#F7F2FA" : "#F7F4FA",
						color: type === "all" ? "#690AA0" : "gray.700",
					}}
					_focus={{
						color: type === "all" ? "#690AA0" : "gray.700",
						boxShadow: "none",
					}}
				>
					<Icon
						fill={type === "all" ? "#690AA0" : "gray.700"}
						as={HomeIcon}
						height="20px"
						width="20px"
					/>
					<Text fontSize="sm">
						{__("All Templates", "magazine-blocks")}
					</Text>
				</HStack>

				<Box px={4} py={3}>
					<Text fontSize="sm" color="gray.700" fontWeight="600">
						{__("Website Parts", "magazine-blocks")}
					</Text>
				</Box>
				{SIDEBAR_ITEMS.map((item) => (
					<HStack
						key={item.type}
						w="full"
						py={2}
						px={4}
						gap="8px"
						bgColor={
							type === item.type ? "purple.50" : "transparent"
						}
						color={type === item.type ? "#690AA0" : "gray.700"}
						borderRadius="md"
						cursor="pointer"
						spacing={3}
						_active={{
							bgColor: "#F7F2FA",
							color: "#690AA0",
						}}
						_hover={{
							bgColor: type === item.type ? "#F7F2FA" : "#F7F4FA",
							color: type === item.type ? "#690AA0" : "gray.700",
						}}
						_focus={{
							color: type === item.type ? "#690AA0" : "gray.700",
							boxShadow: "none",
						}}
						as={Link}
						to={`/site-builder?type=${item.type}`}
					>
						<Icon
							fill={type === item.type ? "#690AA0" : "gray.700"}
							as={item.icon}
							height="20px"
							width="20px"
						/>
						<Text fontSize="sm" _active={{ color: "#690AA0" }}>
							{item.label}
						</Text>
					</HStack>
				))}
			</VStack>
		</Box>
	);
};

export const SiteBuilder: React.FC = () => {
	return (
		<SiteBuilderProvider>
			<Flex
				backgroundColor="#fff"
				border="1px solid #E2E8F0"
				margin="24px"
			>
				<SiteBuilderNav />
				<Box flex="1">
					<Templates onSelectTab={() => {}} />
				</Box>
			</Flex>
		</SiteBuilderProvider>
	);
};

export default SiteBuilder;
